import * as THREE from "three";
import {JSONPath} from "jsonpath-plus";
import Engine from './Engine';
import Utils from './Utilities';
import ARTracker from './ARTracker';
import ProjectSaver from './ProjectSaver';
import TimelineChannel from './timeline/TimelineChannel';
import {TimelineFragment} from './timeline/TimelineFragment';
import TimelineObject from './timeline/TimelineObject';
import {EffectorTitleMap} from "./timeline";
import Scenario from "./scenarioElements/Scenario";
import SceneObjectUtils from "./utilities/SceneObjectUtils";
import MathUtils from "./utilities/MathUtils";
import Callout from "./Callout";
import DefaultSceneManager from './DefaultSceneManager';
import Step from './scenarioElements/Step';
import Sequence from './scenarioElements/Sequence';
import AssetUtility from './AssetUtility';
import {assert} from './utilities/ErrorHandling';
import {Timeline} from "./timeline/Timeline";
import {getUuidv4, JsonCopy} from "./utilities/Misc";
import {
	ChecklistFormType,
	ChecklistPriority,
	ChecklistType,
	CustomEventTypes,
	defaultTriggerObject,
	RenderModes,
	TimelineEffectorTypes,
	TrackerTypes,
	WorldUnitScales
} from '../globals';
import Store, {StoreActions} from '../state/store';
import TemplateTypes from "../components/uisystem/templates/types";
import {ObjectTriggerTypes, VisibilityModes} from "../interfaces/enums";
import {ITimelineParentElementPairs} from "../interfaces/Timeline";
import {IChecklist} from "../interfaces/checklistItem";
import {GjsAttributeNames} from "../components/visageUI/gjsplugin/Enums";
import {IGrapesJSComponent, IVisageTemplate} from "../interfaces/VisageUI";
import sleep from "../classes/utilities/sleep";
import {generateDefaultVisageTemplates} from "../components/visageUI/defaultTemplates/DefaultTemplates";
import StandbyOverlay from "../components/layout/StandbyOverlay";

const enum OccurrenceTypes {
	MEHandle =  "MEHandle",
	MEConsumed = "MEConsumed",
}

interface Occurance {
	OccurrenceType: OccurrenceTypes;
}
interface IRbopItem
{
	Description: string,
	Transformation: number[];
	UID: string;
	Title: string;
	Children: IRbopItem[];
	Path?: string;
}

interface IRbopPartSequence
{
	ItemUID: string;
	OccurrenceType?: OccurrenceTypes;
	Occurrences?: Occurance[];
	Locations: {
		Transformation: number[];
	}[];
	Duration: number;
	NextStepsDisplayMode: string;
}

interface IRbopStep
{
	Title?: string;
	Description?: string;
	PartSequences: IRbopPartSequence[];
	DataCollections?: {
		Name: string;
		Description: string;
		Type: string;
		IsOptional: string;
	}[];
	AdditionalInformation?: {
		c2_asset: string;
	}[];
}

export interface IImportRBOPStep extends IRbopStep {
	selected?: boolean;
	hasContent?: boolean;
	content?: string;
}

export interface IRbopData
{
	asset: string;
	Meta?: 
	{
		DefaultNextStepsPartsDisplayMode?: string;
	}
	Items: IRbopItem[];
	Steps: IRbopStep[];
}

export interface IImportRbopData extends IRbopData
{
	sequenceStepID: string;
	createVersion: string;
	Steps: IImportRBOPStep[];
}

// Type guard function
function isIImportRbopData(obj: any): obj is IImportRbopData 
{
	return (
		typeof obj.sequenceStepID === 'string' &&
        typeof obj.createVersion === 'string' &&
        Array.isArray(obj.Steps)
	);
}

export class RbopUtils
{
	static ThreeMatrixFromRbop(matrixDataArray: number[])
	{
		//matrixDataArray contains row-major left-handed z-up data. Matrix4().set() accepts row-major data, though the matrix elements will be stored in colum major
		tempMatrix.set(
			matrixDataArray[0], matrixDataArray[1], matrixDataArray[2], matrixDataArray[3],
			matrixDataArray[4], matrixDataArray[5], matrixDataArray[6], matrixDataArray[7],
			matrixDataArray[8], matrixDataArray[9], matrixDataArray[10], matrixDataArray[11],
			matrixDataArray[12], matrixDataArray[13], matrixDataArray[14], matrixDataArray[15]
		);

		//convert from right handed z-up to right handed y-up https://discourse.threejs.org/t/convert-from-one-coordinate-system-to-another/13240/10
		const basisChangeMatrix = new THREE.Matrix4().set(
			1,   0,   0,   0,
			0,   0,   1,   0,
			0, - 1,   0,   0,
			0,   0,   0,   1
		);
		const basisChangeMatrixInverse = basisChangeMatrix.clone().transpose(); // because the change-of-basis matrix is orthonormal, its inverse is the same as transpose
		tempMatrix.premultiply(basisChangeMatrix).multiply(basisChangeMatrixInverse);

		//convert positions from mm to meters
		tempMatrix.elements[12] *= WorldUnitScales.Millimeters;
		tempMatrix.elements[13] *= WorldUnitScales.Millimeters;
		tempMatrix.elements[14] *= WorldUnitScales.Millimeters;

		return tempMatrix.clone();
	}

	static determineStepProperties(stepData: IRbopStep)
	{
		const ret =
		{
			containsTrackers: false,
			containsSceneObjects: false,
		};

		if(stepData.PartSequences)
		{
			for (const partSequence of stepData.PartSequences)
			{
				// handle older versions of RBOP data
				if(partSequence.Occurrences?.length) 
				{
					if (partSequence.Occurrences[0].OccurrenceType === OccurrenceTypes.MEHandle)
					{
						ret.containsTrackers = true;
					}
					else
					{
						ret.containsSceneObjects = true;
					}
				}
				else
				{
					if (partSequence.OccurrenceType === OccurrenceTypes.MEHandle)
					{
						ret.containsTrackers = true;
					}
					else
					{
						ret.containsSceneObjects = true;
					}
				}
			}
		} 
		else 
		{
			console.error("No PartSequences found in stepData");
		}

		return ret;
	}

}

const tempPosition = new THREE.Vector3();
const tempQuat = new THREE.Quaternion();
const tempScale = new THREE.Vector3();
const tempMatrix = new THREE.Matrix4();

export default class RbopLoader
{
	data: IImportRbopData;
	defaultContinuedVisibilityMode: VisibilityModes;
	itemLookup : Map<string, IRbopItem>;
	assetRootLookup: Map<string, THREE.Object3D>;
	timelineParentElementPairs: ITimelineParentElementPairs[];

	constructor(rbopData: IImportRbopData)
	{
		this.data = rbopData;
		this.defaultContinuedVisibilityMode = this.data?.Meta?.DefaultNextStepsPartsDisplayMode ?
			this.data.Meta.DefaultNextStepsPartsDisplayMode.toLowerCase() as VisibilityModes: VisibilityModes.Solid;
		this.itemLookup = new Map();
		this.assetRootLookup = new Map();
		this.timelineParentElementPairs = [];
		this.populateItemLookup();
	}

	load = async () =>
	{
		try
		{
			if(!this.data.asset) return;

			const assetUrl = AssetUtility.getProcessedAssetURL(this.data.asset);
			const assetName = AssetUtility.getAssetName(this.data.asset)!;

			await Engine.getInstance().prepareModel(this.data.asset, assetUrl, assetName, undefined, false, true);
			
			StandbyOverlay.updateStandbyModeMessage("Loading Project", true);
			await sleep();
			this.onAssetsLoaded();
			
		}
		catch (error)
		{
			console.error('project loading failed', error);
			throw error;
		}
	}

	onAssetsLoaded = () =>
	{
		try
		{
			const rootAsset = Engine.getInstance().getModelAsset(this.data.asset);
			if(rootAsset)
			{
				rootAsset.traverse((child) =>
				{
					if(child.userData.name)
					{
						this.assetRootLookup.set(child.userData.name, child);
						delete child.userData.name;
					}
				});

				Store.dispatch(StoreActions.ui.loadSlideMasterData({ shouldConvert: {}, ui: {} }));

				if((this.data as IImportRbopData)?.sequenceStepID)
				{
					if(this.data?.Steps?.length)
					{
						const sequence = Scenario.getSequence(this.data.sequenceStepID);
						if(sequence?.steps)
						{ 
							this.generateSteps(sequence, false, sequence.steps.length, false);
						}
						else 
						{
							const step = Scenario.getSteps(true).find(step => step.id === this.data.sequenceStepID);
							if(step)
							{
								const idx = step.sequence.steps.findIndex(step => step.id === this.data.sequenceStepID);
								this.generateSteps(step.sequence, false, idx, false);
							} 
							else 
							{
								throw new Error("Invalid sequenceStepID");
							}
						}
					}
				}
				else 
				{
					const intialSequence = Scenario.initialSequence();
					if(intialSequence)
					{
						Scenario.clear();
						intialSequence.dispose();
					}
					const sequence = new Sequence();
					Scenario.addSequence(sequence);
					Store.dispatch(StoreActions.scenario.setInitialSequence(sequence.id));
					for (const step of sequence.steps)
					{
						step.dispose();
					}
					sequence.steps.length = 0;

					if (this.data?.Steps?.length)
					{
						this.generateSteps(sequence);
					}
				}
			}
		}
		catch (e)
		{
			console.error(e);
			throw e;
		}

		this.finalize();
	}

	finalize = () =>
	{
		this.itemLookup.clear();
		this.assetRootLookup.clear();
		this.timelineParentElementPairs.length = 0;
		window.dispatchEvent(new Event('resize'));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onProjectLoadEnd));
	}

	populateItemLookup = (searchRoot = this.data?.Items) =>
	{
		if(!searchRoot) return;

		for (const item of searchRoot)
		{
			this.itemLookup.set(item.UID, item);
			if (item.Children?.length)
			{
				this.populateItemLookup(item.Children);
			}
		}
	}

	hasMEHandle = () =>
	{
		for (let i = 0; i < this.data.Steps.length; i++)
		{
			const stepData = this.data.Steps[i];
			for (const partSequence of stepData.PartSequences)
			{
				if(partSequence.OccurrenceType === OccurrenceTypes.MEHandle) return true;
			}
		}
		return false;
	}

	generateSteps = (sequence: Sequence, addContentPlacementStep: boolean = true, idx: number = 0, isStepSelected: boolean = true) =>
	{
		const contentPlacementStep = addContentPlacementStep ? new Step(sequence, { isTrackersStep: true, dontMakeTracker: true }) : Scenario.getCPS();
		if(!contentPlacementStep) throw new Error("No Content Placement Step found");
		
		//get templates and default visual template
		let uiTemplateDatas = Store.getState().visageModule.uiTemplateData;
		if(Object.keys(uiTemplateDatas).length === 0) 
		{
			Store.dispatch(StoreActions.visage.loadUIData({ stepUIData: {}, uiTemplateData: { ...generateDefaultVisageTemplates() }, cameraCanvasPairings: {}, stepTemplateMap: {} }));
			uiTemplateDatas = Store.getState().visageModule.uiTemplateData;
		}
		const defaultVisualTemplate = Object.values(uiTemplateDatas).find((value) => value.name.toLocaleLowerCase() == 'content');
		if(!defaultVisualTemplate) throw new Error("No default visual template found");

		addContentPlacementStep && Scenario.addStep(sequence, contentPlacementStep);

		sequence.trackersStep = contentPlacementStep;
		let contentPlacementStepInitialized = !addContentPlacementStep;
		let continuedContentPlacementStep = false;
		let previousStep: Step | null = null;
		let partSequencesToAdd: IRbopPartSequence[] = [];
		
		for (let i = 0; i < this.data.Steps.length; i++)
		{
			const stepData = this.data.Steps[i];
			const stepProperties = RbopUtils.determineStepProperties(stepData);
			const isSelected = stepData.selected || isStepSelected;

			if (stepProperties.containsTrackers)
			{
				if (!contentPlacementStepInitialized)
				{
					sequence.moveStep(0, i);
					contentPlacementStepInitialized = true;
				}

				if(addContentPlacementStep)
				{
					Store.dispatch(StoreActions.visage.setStepTemplate({ stepId: contentPlacementStep.id, templateId: defaultVisualTemplate.id }));
					this.addTemplateToStep(contentPlacementStep, defaultVisualTemplate, stepData.Description || stepData.Title || "");
				}

				if(stepData.PartSequences)
				{
					for (const partSequence of stepData.PartSequences)
					{
						if (partSequence.OccurrenceType !== OccurrenceTypes.MEHandle) continue;
						if (addContentPlacementStep && partSequence.Locations)
						{
							const trackerObject = this.generateObjectFromPartSequence(partSequence);
							const map = Engine.getInstance().getSelectionRoot(contentPlacementStep.sceneId);
							
							map.attach(trackerObject);
							trackerObject.userData.visibility[RenderModes.Device] = VisibilityModes.Hidden;
									
							const modelTracker = new ARTracker(TrackerTypes.MODEL);
							modelTracker.setModelFromSceneSelection([trackerObject]);
							modelTracker.setPoseAutoInit(false);
		
							const surfaceTracker = new ARTracker(TrackerTypes.SLAM);
							const trackerObjectBounds = new THREE.Box3();
							SceneObjectUtils.expandBoundsByObjectVisibleOnly(trackerObjectBounds, trackerObject);
							surfaceTracker.position.set(0.0, trackerObjectBounds.min.y, trackerObjectBounds.max.z);
		
							ARTracker.trackersRoot.add(modelTracker);
							ARTracker.trackersRoot.add(surfaceTracker);
							if(contentPlacementStep.trackers)
							{
								contentPlacementStep.trackers.push(modelTracker.userData.guid);
								contentPlacementStep.trackers.push(surfaceTracker.userData.guid);
							}
							
						}
					}
				}
			}

			if (stepProperties.containsSceneObjects)
			{
				if(isSelected)
				{
					const step = previousStep ? previousStep.clone({ continues: true }) : new Step(sequence, { });
					if(stepData.Title) step.name = stepData.Title;

					if(addContentPlacementStep && contentPlacementStepInitialized)
					{
						Store.dispatch(StoreActions.visage.setStepTemplate({ stepId: step.id, templateId: defaultVisualTemplate.id }));
						this.addTemplateToStep(contentPlacementStep, defaultVisualTemplate, stepData.Description || stepData.Title || "");
					}
		
					this.generateStepChecklist(stepData, step);
					this.generateStepAttachments(stepData, step);
						
					addContentPlacementStep ? Scenario.addStep(sequence, step) : Scenario.addStep(sequence, step, idx + i + 1);
		
					const visualTemplate = stepData.content ? uiTemplateDatas[stepData.content] : defaultVisualTemplate;
					Store.dispatch(StoreActions.visage.setStepTemplate({ stepId: step.id, templateId: visualTemplate.id }));
						
					this.addTemplateToStep(step, visualTemplate!, stepData.Description || stepData.Title || "");
						
					//handle visibility of continued objects
					const sceneRoot = Engine.getInstance().getSelectionRoot(step.sceneId);

					sceneRoot.traverse(child =>
					{
						if (child.userData.visibility?.continuedVisibilityMode)
						{
							child.userData.visibility[RenderModes.Device] = child.userData.visibility.continuedVisibilityMode;
							delete child.userData.visibility.continuedVisibilityMode;
						}
					});
		
					//manually "continue" CPS in the step that follows it
					if (contentPlacementStepInitialized && !continuedContentPlacementStep)
					{
						for (const contentPlacementStepObject of Engine.getInstance().getSelectionRoot(contentPlacementStep.sceneId).children)
						{
							if (!DefaultSceneManager.getInstance().getDefaultObject(contentPlacementStepObject.userData.guid))
							{
								const map = Engine.getInstance().getSelectionRoot(step.sceneId)
								const object = SceneObjectUtils.cloneObject(contentPlacementStepObject);
								map.getObjectByName(contentPlacementStepObject.name) && map.attach(object);
							}
						}
						continuedContentPlacementStep = true;
					}

					// add current sequences
					partSequencesToAdd = partSequencesToAdd.concat(stepData.PartSequences);

					if(partSequencesToAdd.length > 0)
					{
						for (const partSequence of partSequencesToAdd)
						{
							if (partSequence.OccurrenceType === OccurrenceTypes.MEHandle) continue;
							if (partSequence.Duration && step?.timeline) step.timeline.loopTime = partSequence.Duration;
							if (partSequence.Locations)
							{
								const object = this.generateObjectFromPartSequence(partSequence);
								Engine.getInstance().getSelectionRoot(step?.sceneId).attach(object);
							}
						}
					}

					if(!previousStep) Scenario.setCurrentStep(step);
					previousStep = step;

					//clear to avoid duplicates
					partSequencesToAdd = [];
				} 
				else
				{
					partSequencesToAdd = partSequencesToAdd.concat(stepData.PartSequences);
				}
			}
		}

		this.timelineParentElementPairs.forEach((parentElementPair) =>
		{
			parentElementPair.parent.getChildren().push(parentElementPair.element);
			parentElementPair.element.onAdded();
		});

		if (contentPlacementStep.trackers!.length)
		{
			const tracker = new ARTracker(TrackerTypes.SLAM);
			ARTracker.trackersRoot.add(tracker);
			contentPlacementStep.trackers!.push(tracker.userData.guid);
		}

		// add empty last step with end procedure UI
		if(addContentPlacementStep) 
		{
			const exitTemplate = Object.values(uiTemplateDatas).find((value) => value.name.toLocaleLowerCase() == 'exit scenario');
			if(!exitTemplate) throw new Error("No default visual template found");

			const step = new Step(sequence, { });
			step.name = "Final Step";
			Scenario.addStep(sequence, step);
			Store.dispatch(StoreActions.visage.setStepTemplate({ stepId: step.id, templateId: exitTemplate.id }));
			this.addTemplateToStep(step, exitTemplate, "Final Step");
		}
	}

	addTemplateToStep = (step: Step, visageTemplate: IVisageTemplate, content: string) => 
	{
		const uiTemplateData = JsonCopy(visageTemplate.content);
		const textComponent = JSONPath<IGrapesJSComponent[]>({ path: `$..components[?(@.attributes['${GjsAttributeNames.SmartIdentifier}'])]`, json: uiTemplateData })?.at(0);
		if(textComponent)
		{
			textComponent.content = content;
		}
		Store.dispatch(StoreActions.visage.setStepUIData({ stepId: step.id, data: uiTemplateData }));
	}

	generateObjectFromPartSequence = (partSequence: IRbopPartSequence) =>
	{
		const item = this.itemLookup.get(partSequence.ItemUID);
		
		if(!item) throw new Error("Invalid itemUID");

		let object: THREE.Object3D;

		if (item.Path)
		{
			const assetRoot = this.assetRootLookup.get(item.Path)!;
			object = SceneObjectUtils.cloneObject(assetRoot);
		}
		else
		{
			object = new THREE.Object3D();
			SceneObjectUtils.initialiseObjectProperties(object);
		}

		object.name = item.Title ?? "";
		object.userData.visibility.continuedVisibilityMode = partSequence.NextStepsDisplayMode ?
			partSequence.NextStepsDisplayMode.toLowerCase() : this.defaultContinuedVisibilityMode;
		object.userData.visibility.overrideParent = true;

		const transformMatrix = RbopUtils.ThreeMatrixFromRbop(partSequence.Locations[0].Transformation);
		transformMatrix.multiply(object.matrixWorld);
		transformMatrix.decompose(object.position, object.quaternion, object.scale);
		
		this.generateDefaultAdditionsForObject(object);
		ProjectSaver.getInstance().markAsSaveRoot(object);

		if(item.Children)
		{
			for (const childItem of item.Children)
			{
				const childObject = this.generateObjectFromItem(childItem, partSequence);
				object.attach(childObject);
			}
		}

		return object;
	}

	generateObjectFromItem = (item: IRbopItem, partSequence: IRbopPartSequence) =>
	{
	
		let object: THREE.Object3D;
	
		if (item.Path)
		{
			const assetRoot = this.assetRootLookup.get(item.Path)!;
			object = SceneObjectUtils.cloneObject(assetRoot);
		}
		else
		{
			object = new THREE.Object3D();
			SceneObjectUtils.initialiseObjectProperties(object);
		}
	
		object.name = item.Title ?? "";
		object.userData.visibility.continuedVisibilityMode = partSequence.NextStepsDisplayMode ?
			partSequence.NextStepsDisplayMode.toLowerCase() : this.defaultContinuedVisibilityMode;
		object.userData.visibility.overrideParent = true;
	
		const transformMatrix = RbopUtils.ThreeMatrixFromRbop(item.Transformation);
		transformMatrix.multiply(object.matrixWorld);
		transformMatrix.decompose(object.position, object.quaternion, object.scale);

		if(item.Children)
		{
			object.userData.markedSaveRoot = true;
			for (const childItem of item.Children)
			{
				const childObject = this.generateObjectFromItem(childItem, partSequence);
				object.attach(childObject);
			}
		}
	
		return object;
	}

	generateDefaultAdditionsForObject = (object: THREE.Object3D) =>
	{
		if(object instanceof THREE.Mesh)
		{
			if (!object.geometry.boundingBox) object.geometry.computeBoundingBox();
			const originOffset = new THREE.Vector3();
			object.geometry.boundingBox.getCenter(originOffset);

			const callout = Callout.addCallout(object);
			callout.setTitle("#ObjectName");
			callout.setDescription("");
			callout.setDescriptionEnabled(false);
			callout.setStrokeEnabled(false);
			callout.setVisible(false);
			callout.setScale(1.0);
			callout.setAutoWidth(true);
			callout.setTitleColor({r: 255, g: 255, b: 255});
			callout.setTitleBackground({r: 0, g: 0, b: 0, a : 100});
			callout.setTextColor({r: 255, g: 255, b: 255});
			callout.setBackgroundColor({r: 0, g: 0, b: 0, a: 100});
			callout.setLineColor({r: 0, g: 0, b: 0, a: 100});
			callout.setOriginOffset(originOffset);
			callout.setOffset(new THREE.Vector3(0.15, 0.15, 0));

			const visibilityTrigger =
			{
				type: ObjectTriggerTypes.VisibilityToggle,
				targetObject: object.userData.guid,
				targetObjectVisibility: VisibilityModes.Ghosted,
			}
			const calloutTrigger =
			{
				type: ObjectTriggerTypes.CalloutToggle,
				targetObject: object.userData.guid,
			}

			object.userData.triggers = [
				{ ...JSON.parse(JSON.stringify(defaultTriggerObject)), ...visibilityTrigger },
				{ ...JSON.parse(JSON.stringify(defaultTriggerObject)), ...calloutTrigger },
			];
		}
	}

	generateTimelineAnimations = (partSequence: IRbopPartSequence, object: THREE.Object3D, timeline: Timeline) =>
	{
		if (partSequence.Locations.length < 2 || partSequence.OccurrenceType === OccurrenceTypes.MEHandle) return;

		const item = this.itemLookup.get(partSequence.ItemUID)!;
		assert(item, "invalid itemUID");

		const initialWorldMatrix = new THREE.Matrix4();

		if (item.Path)
		{
			const assetRoot = this.assetRootLookup.get(item.Path)!;
			initialWorldMatrix.copy(assetRoot.matrixWorld);
		}

		const previousPosition = new THREE.Vector3().copy(object.position);
		const previousQuaternion = new THREE.Quaternion().copy(object.quaternion);
		const previousScale = new THREE.Vector3().copy(object.scale);
		
		const timelineObjectParams = { track: timeline.objectsTrack!, skipChannelCreation: true, sceneObjectReference: object};
		const timelineObject = new TimelineObject(timelineObjectParams);
		this.timelineParentElementPairs.push({ parent: timeline.objectsTrack!, element: timelineObject });

		const animationLength =  partSequence.Duration ? partSequence.Duration / (partSequence.Locations.length - 1) : 3.0;
		for (let i = 1; i < partSequence.Locations.length; i++)
		{
			const animationIdex = i-1;
			const locationTransformMatrix = RbopUtils.ThreeMatrixFromRbop(partSequence.Locations[i].Transformation);
			locationTransformMatrix.multiply(initialWorldMatrix);
			locationTransformMatrix.decompose(tempPosition, tempQuat, tempScale);
			const deltaPosition = new THREE.Vector3().subVectors(tempPosition, previousPosition);

			if (deltaPosition.lengthSq() > 0.00001)
			{
				const newChannel = new TimelineChannel(Utils.getUniqueElementName(timelineObject.channels, "Position Channel"), timelineObject);
				this.timelineParentElementPairs.push({ parent: timelineObject, element: newChannel });
				const proposedName = EffectorTitleMap.get(TimelineEffectorTypes.TransformTranslate);
				if (proposedName)
				{
					const newFragment = new TimelineFragment(Utils.getUniqueElementName(newChannel.fragments, proposedName), newChannel, TimelineEffectorTypes.TransformTranslate, animationLength * animationIdex, animationLength * animationIdex + animationLength);
					this.timelineParentElementPairs.push({parent: newChannel, element: newFragment});
					newFragment.effector.translation.copy(deltaPosition);
				}
			}

			const deltaRotation = MathUtils.getShortestDeltaQuaternion(previousQuaternion, tempQuat);
			const axisAngle = MathUtils.quaternionToAxisAngle(deltaRotation);
			if (axisAngle.angle > 0.0)
			{
				const newChannel = new TimelineChannel(Utils.getUniqueElementName(timelineObject.channels, "Rotate Channel"), timelineObject);
				this.timelineParentElementPairs.push({ parent: timelineObject, element: newChannel });
				const proposedName =EffectorTitleMap.get(TimelineEffectorTypes.TransformRotate);
				if (proposedName)
				{
					const newFragment = new TimelineFragment(Utils.getUniqueElementName(newChannel.fragments, proposedName), newChannel, TimelineEffectorTypes.TransformRotateSelf, animationLength * animationIdex, animationLength * animationIdex + animationLength);
					this.timelineParentElementPairs.push({parent: newChannel, element: newFragment});
					newFragment.effector.axis.set(axisAngle.axis.x, axisAngle.axis.y, axisAngle.axis.z);
					newFragment.effector.angle = axisAngle.angle;
				}
			}

			locationTransformMatrix.decompose(previousPosition, previousQuaternion, previousScale);
		}
	}

	generateStepUI = (stepData: IRbopStep, step: Step, cpsInitialized = true) =>
	{
		const slideTemplate = { ...Store.getState().uiModule.slideMaster[TemplateTypes.CONTENT], slide: TemplateTypes.CONTENT, synced: false };

		// determine if there's text description
		let textDescription = { ...slideTemplate[TemplateTypes.CONTENT].TextDescription, visible: {
			type: 'toggle',
			default: true,
			value: false
		} };
		if (stepData.Title || stepData.Description)
		{
			textDescription = { ...slideTemplate[TemplateTypes.CONTENT].TextDescription,
				description: {
					...slideTemplate[TemplateTypes.CONTENT].TextDescription.description, value: stepData.Title ? stepData.Title + '\n\n' + stepData.Description : stepData.Description
				},
				visible: {
					type: 'toggle',
					default: true,
					value: true
				},
				preset: cpsInitialized || !this.hasMEHandle() ? {
					type: 'text-area-preset',
					default: 'headers-bottom',
					value: 'headers-bottom'
				} : {
					type: 'text-area-preset',
					default: 'headers-bottom',
					value: 'headers-top'
				} };
		}

		// generate slide
		const uiTemplate = {
			[TemplateTypes.CONTENT]: {
				NextStep: { ...slideTemplate[TemplateTypes.CONTENT].NextStep },
				PreviousStep: { ...slideTemplate[TemplateTypes.CONTENT].PreviousStep },
				TextDescription: textDescription,
				Voice: { ...slideTemplate[TemplateTypes.CONTENT].Voice },
				Insets: { ...slideTemplate[TemplateTypes.CONTENT].Insets },
				Headers: { ...slideTemplate[TemplateTypes.CONTENT].Headers,
					"visible": { ...slideTemplate[TemplateTypes.CONTENT].Headers["visible"], value: false },
				},
			},
			active: slideTemplate.active,
			slide: TemplateTypes.CONTENT,
			synced: false,
		};

		Store.dispatch(StoreActions.ui.setFromCopy({ stepId: step.id, content: { ...uiTemplate } }));
		const newSlideTemplate = { ...Store.getState().uiModule.ui[step.id], name: `(${step.sequence.name}).(${step.name})_Template`, type: 'slide' };
		delete newSlideTemplate.synced;
		Store.dispatch(StoreActions.ui.setSlideFromCopy({ slide: getUuidv4(), content: { ...newSlideTemplate }, sourceStepId: step.id }));
	}

	generateEndProcedureUI = (step: Step) =>
	{
		const slideTemplate = { ...Store.getState().uiModule.slideMaster[TemplateTypes.FINISH_PROCEDURE], slide: TemplateTypes.FINISH_PROCEDURE, synced: false };
		const uiTemplate = {
			[TemplateTypes.FINISH_PROCEDURE]: {
				Container: { ...slideTemplate[TemplateTypes.FINISH_PROCEDURE].Container },
				Buttons: { ...slideTemplate[TemplateTypes.FINISH_PROCEDURE].Buttons },
				PreviousStep: { ...slideTemplate[TemplateTypes.FINISH_PROCEDURE].PreviousStep },
				Voice: { ...slideTemplate[TemplateTypes.FINISH_PROCEDURE].Voice }
			},
			active: slideTemplate.active,
			slide: TemplateTypes.FINISH_PROCEDURE,
			synced: false,
		};
		Store.dispatch(StoreActions.ui.setFromCopy({ stepId: step.id, content: { ...uiTemplate } }));
		const newSlideTemplate = { ...Store.getState().uiModule.ui[step.id], name: `(${step.sequence.name}).(${step.name})_Template`, type: 'slide' };
		delete newSlideTemplate.synced;
		Store.dispatch(StoreActions.ui.setSlideFromCopy({ slide: getUuidv4(), content: { ...newSlideTemplate }, sourceStepId: step.id }));
	}

	generateStepChecklist = (stepData: IRbopStep, step: Step) =>
	{
		Store.dispatch(StoreActions.checklist.setFromCopy({ stepId: step.id, content: null })); //empty checklist in case it was cloned
		if(!stepData.DataCollections || stepData.DataCollections.length <= 0) return;

		const checklistItemTypeMap = {
			'Boolean Data Collection' : ChecklistType.CHECKBOX,
			'String Data Collection' : ChecklistType.TEXT,
			'Real Number Data Collection' : ChecklistType.NUMBER,
		}

		const stepId = step.id;
		const checklist: IChecklist = {
			behaviour : ChecklistPriority.PASSIVE,
			next: "",
			type: ChecklistFormType.FORM,
			questions: [],
			items: [],
		};

		for(let i = 0; i< stepData.DataCollections.length; i++)
		{
			const rawItem = stepData.DataCollections[i];

			//if any of the items is a 'must respond' set the checklist to force view
			if(rawItem.IsOptional.toLowerCase() === "false")
			{
				checklist.behaviour = ChecklistPriority.FORCEVIEW;
			}

			checklist.items.push({
				type: checklistItemTypeMap[rawItem.Type],
				id: i,
				description: rawItem.Description ? rawItem.Description : rawItem.Name,
				mustRespond: rawItem.IsOptional.toLowerCase() === "false",
				options: [],
			})
		}

		Store.dispatch(StoreActions.checklist.setFromCopy({ stepId: stepId, content: checklist }));
	}

	generateStepAttachments = (stepData: IRbopStep, step: Step) =>
	{
		if (stepData.AdditionalInformation)
		{
			const stepAttachments: string[] = [];
			for (let i = 0; i < stepData.AdditionalInformation.length; i++ )
			{
				stepAttachments.push(stepData.AdditionalInformation[i].c2_asset);
			}

			Store.dispatch(StoreActions.attachments.setStepAttachments({stepId: step.id, assetIds: stepAttachments }));
		}
	}
}
